import { computed, inject, Injectable, signal } from '@angular/core';
import { z } from 'zod';
import { Announcement } from './announcement.model';
import { AnnouncementStorage } from './announcement.storage';
import { LaunchDarklyService, StructuredFeatureFlags } from '@shared/services/launch-darkly.service';

type ActiveAnnouncement = Announcement;

const announcementSchema = z.object({
  id: z.string().trim().min(1),
  titleKey: z.string(),
  messageKey: z.string(),
  url: z.string().optional(),
  linkTextKey: z.string().optional(),
  enabled: z.boolean(),
});

@Injectable({ providedIn: 'root' })
export class AnnouncementService {
  private readonly storage = inject(AnnouncementStorage);
  private readonly launchDarkly = inject(LaunchDarklyService);
  private readonly seenAnnouncementIds = signal<ReadonlySet<string>>(new Set());

  private readonly announcement = this.launchDarkly.structuredVariation(StructuredFeatureFlags.Announcement, null);

  readonly activeAnnouncement = computed<ActiveAnnouncement | null>(() => {
    const parsedAnnouncement = announcementSchema.safeParse(this.announcement());
    if (!parsedAnnouncement.success) {
      return null;
    }

    const announcement = parsedAnnouncement.data;
    if (!this.isActive(announcement) || this.isSeen(announcement.id)) {
      return null;
    }

    return announcement;
  });

  markSeen(id: string): void {
    this.storage.markSeen(id);
    this.seenAnnouncementIds.update((ids) => new Set(ids).add(id));
  }

  private isActive(announcement: Announcement): boolean {
    return Boolean(announcement.enabled && announcement.titleKey?.trim() && announcement.messageKey?.trim());
  }

  private isSeen(id: string): boolean {
    return this.seenAnnouncementIds().has(id) || this.storage.isSeen(id);
  }
}
